import { useEffect, useState, useCallback } from 'react'
import { useApp } from '../context/AppContext'
import { sileo } from 'sileo'

/**
 * Custom hook to manage users and their KPI subscriptions.
 * Handles data loading, filtering, and subscription logic.
 *
 * @returns {Object} Users state and handlers.
 * @returns {Array<Object>} returns.allUsers - Full list of users.
 * @returns {Array<Object>} returns.filteredUsers - Filtered list of users.
 * @returns {Array<Object>} returns.subs - List of KPI user subscriptions.
 * @returns {boolean} returns.loading - Indicates whether data is being loaded.
 * @returns {Function} returns.loadData - Reloads users and subscriptions.
 * @returns {Function} returns.handleSearch - Filters users based on criteria.
 * @returns {Function} returns.subscribeUser - Subscribes a user to KPIS.
 */
const useUsers = () => {
  const { supabase } = useApp()

  const [allUsers, setAllUsers] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])
  const [subs, setSubs] = useState([])
  const [loading, setLoading] = useState(true)

  /**
   * Loads users and KPI subscriptions in parallel.
   *
   * @async
   * @returns {Promise<void>}
   */
  const loadData = useCallback(async () => {
    setLoading(true)

    try {
      const [usersRes, subsRes] = await Promise.all([
        supabase.schema('public').from('Empleados').select('*'),
        supabase.schema('KPIS').from('User').select('*'),
      ])

      if (usersRes.error) throw usersRes.error
      if (subsRes.error) throw subsRes.error

      const users = usersRes.data || []
      const subscriptions = subsRes.data || []

      setAllUsers(users)
      setFilteredUsers(users)
      setSubs(subscriptions)
    } catch (err) {
      console.error(err)
      sileo.error({
        title: 'Error cargando usuarios',
      })
    } finally {
      setLoading(false)
    }
  }, [supabase])

  /**
   * Automatically loads data on mount.
   */
  useEffect(() => {
    loadData()
  }, [loadData])

  /**
   * Subscribes a user to KPIS if not already subscribed.
   *
   * @param {number|string} userId - User ID (Empleados table).
   * @param {string} authId - Authentication ID.
   */
  const subscribeUser = useCallback(
    async (userId, authId) => {
      const alreadySub = subs.some((s) => s.UserId === userId)

      if (alreadySub) {
        sileo.info({ title: 'El usuario ya está suscrito' })
        return
      }

      try {
        const { data, error } = await supabase
          .schema('KPIS')
          .from('User')
          .insert([
            {
              UserMainId: userId,
              Enable: true,
              AuthId: authId,
              IsSupervisor: false,
            },
          ])
          .select()
          .single()

        if (error) throw error

        setSubs((prev) => [...prev, data])
        sileo.success({
          title: 'Usuario suscrito correctamente',
        })
      } catch (err) {
        console.error(err)
        sileo.error({
          title: 'No se pudo suscribir al usuario',
        })
      }
    },
    [subs, supabase]
  )

  /**
   * Filters users based on name, department, and subscription status.
   *
   * @param {Object} filters - Filtering criteria.
   * @param {string} [filters.empleado] - Employee name filter.
   * @param {number|string} [filters.departamento] - Department ID filter.
   * @param {boolean} [filters.suscritos] - Filter only subscribed users.
   */
  const handleSearch = useCallback(
    (filters) => {
      const filtered = allUsers.filter((user) => {
        const nameMatch = !filters.empleado
          ? true
          : user.nombre?.toLowerCase().includes(filters.empleado.toLowerCase())

        const deptMatch = !filters.departamento
          ? true
          : user.departamento_id === Number(filters.departamento)

        const subsMatch = !filters.suscritos
          ? true
          : subs.some((s) => s.User_Id === user.id)

        return nameMatch && deptMatch && subsMatch
      })

      setFilteredUsers(filtered)
    },
    [allUsers, subs]
  )

  return {
    allUsers,
    filteredUsers,
    subs,
    loading,
    loadData,
    handleSearch,
    subscribeUser,
  }
}

export default useUsers
